import Service, { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import LongLivedBasket from 'my-phorest/gql/mutations/long-lived-basket.graphql';
import { today } from 'my-phorest/utils/local-date-helpers';
import AppointmentIdsByDate from 'my-phorest/gql/queries/appointment-ids-by-date.graphql';
import { fetchAllPaginatedResults } from 'my-phorest/utils/graphql';
import { NO_UNPAID_APPOINTMENTS_IN_LONG_LIVED_BASKET } from 'my-phorest/utils/purchase-helpers';
import { STATES } from 'my-phorest/utils/calendar-events';

export default class ReuseBasketService extends Service {
  @queryManager apollo;
  @service fullCalendar;
  @service intl;
  @service notifications;
  @service paymentSlideOver;
  @service purchase;
  @service session;

  @task
  *loadLongLivedBasketTask(clientId, appointmentIds, currentDate) {
    let date = currentDate ?? today();
    let terminalId = yield this.paymentSlideOver.getTerminalId.perform();
    let response = yield this.apollo.mutate(
      {
        mutation: LongLivedBasket,
        variables: {
          clientId,
          date,
          appointmentIds,
          terminalId,
          staffMemberId: this.session.currentStaffMemberId,
        },
        fetchPolicy: 'network-only',
      },
      'longLivedBasket'
    );
    this.#handlePotentialDesynchronization(appointmentIds, response.basket);

    return response.basket;
  }

  @task
  *loadLongLivedBasketWithTodayAppointmentsTask(clientId) {
    let appointmentsIds =
      yield this.loadTodayUnpaidAppointmentsForClientTask.perform(clientId);
    return yield this.loadLongLivedBasketTask.perform(
      clientId,
      appointmentsIds
    );
  }

  @task
  *loadTodayUnpaidAppointmentsForClientTask(clientId) {
    if (!clientId) return [];
    let appointments = yield fetchAllPaginatedResults(
      this.apollo,
      {
        query: AppointmentIdsByDate,
        variables: {
          filterBy: {
            clientId,
            startDate: today(),
            endDate: today(),
          },
        },
        fetchPolicy: 'network-only',
      },
      'appointments'
    );
    if (appointments.length) {
      return appointments
        .filter((appointment) => appointment.state !== STATES.paid)
        .map((a) => a.id);
    } else {
      return [];
    }
  }

  /**
   * Handles potential desynchronization between the provided list of appointment IDs
   * and the returned basket's appointments. Long-lived basket filters out appointments that were already paid.
   * If a mismatch is detected, the calendar cache is evicted, data refetched, and the user notified about the
   * desynchronization.
   *
   * @param {Array<string>} appointmentIds
   * @param {Object} basket
   * @return {void}
   */
  #handlePotentialDesynchronization(appointmentIds, basket) {
    let basketAppointmentIds = basket.items
      .filter((item) => !!item.appointment)
      .map((item) => item.appointment.id);
    let atLeastOneAppointmentIdNotIncludedInBasketItems = appointmentIds.some(
      (appointmentId) => !basketAppointmentIds.includes(appointmentId)
    );

    if (
      appointmentIds.length > 0 &&
      atLeastOneAppointmentIdNotIncludedInBasketItems
    ) {
      this.purchase.handleAlreadyPaidAppointmentPayAttempt();
      this.fullCalendar.calendarApi?.refetchResources();

      this.notifications.info(
        this.intl.t(
          'appointments.synchronization.appointment-already-paid.title'
        ),
        this.intl.t(
          'appointments.synchronization.appointment-already-paid.description'
        ),
        { sticky: true }
      );

      // If filtering results in no items at all, then raise an error and handle locally. E.g., redirect to the calendar.
      if (basket.items.length === 0) {
        throw new Error(NO_UNPAID_APPOINTMENTS_IN_LONG_LIVED_BASKET);
      }
    }
  }
}
